import { useMemo, useState } from 'react';
import { X, Printer, DownloadSimple, Tag } from '@phosphor-icons/react';
import JsBarcode from 'jsbarcode';
import IOSToggle from './IOSToggle';

/**
 * LabelExportModal — export barcode-labels naar PDF voor geselecteerde of alle artikelen.
 *
 * Ondersteunt rol-printers (Dymo / Brother / Zebra) én A4-vellen (Avery etc.) + custom.
 */

// Afmetingen in mm
export const LABEL_FORMATS = {
  dymo_99012:      { label: 'Dymo 99012 (89×36 mm)',             type: 'roll',  w: 89,  h: 36,  note: 'Dymo LabelWriter adreslabels' },
  dymo_11356:      { label: 'Dymo 11356 (89×41 mm)',             type: 'roll',  w: 89,  h: 41,  note: 'Dymo S0722560 naambadge' },
  brother_dk1201:  { label: 'Brother DK-1201 (90×29 mm)',        type: 'roll',  w: 90,  h: 29,  note: 'Brother QL adreslabels' },
  brother_dk1202:  { label: 'Brother DK-1202 (100×62 mm)',       type: 'roll',  w: 100, h: 62,  note: 'Brother QL verzendlabels' },
  brother_dk22205: { label: 'Brother DK-22205 (62 mm eindeloos)', type: 'roll',  w: 62,  h: 40,  note: 'Continu rol, zelf hoogte bepalen' },
  zebra_2x1:       { label: 'Zebra 51×25 mm',                    type: 'roll',  w: 51,  h: 25,  note: 'Kleine retail/voorraad' },
  custom_60x30:    { label: 'Custom rol 60×30 mm',               type: 'roll',  w: 60,  h: 30,  note: 'Universeel kassa-formaat' },
  avery_l7159:     { label: 'Avery L7159 A4 (64×47 mm — 24/vel)', type: 'a4',    w: 63.5, h: 46.6, cols: 3, rows: 8, mLeft: 7.2, mTop: 15.15, gapX: 2.5, gapY: 0,  note: '24 labels per A4-vel' },
  avery_l7160:     { label: 'Avery L7160 A4 (64×38 mm — 21/vel)', type: 'a4',    w: 63.5, h: 38.1, cols: 3, rows: 7, mLeft: 7.2, mTop: 15.15, gapX: 2.5, gapY: 0,  note: '21 labels per A4-vel' },
  avery_l7163:     { label: 'Avery L7163 A4 (99×38 mm — 14/vel)', type: 'a4',    w: 99.1, h: 38.1, cols: 2, rows: 7, mLeft: 4.65, mTop: 15.15, gapX: 2.5, gapY: 0, note: '14 labels per A4-vel' },
  custom:          { label: 'Aangepast formaat…',                type: 'custom', w: 60, h: 30 },
};

function renderBarcodeSvg(value) {
  if (!value) return '';
  try {
    const svg = document.createElement('svg');
    JsBarcode(svg, value.toString(), {
      format: /^\d{13}$/.test(value) ? 'EAN13' : (/^\d{8}$/.test(value) ? 'EAN8' : 'CODE128'),
      height: 34, width: 1.4, margin: 0, displayValue: true, fontSize: 9, textMargin: 1,
      background: '#fff', lineColor: '#000',
    });
    // serializeToString voor gebruik in innerHTML
    return new XMLSerializer().serializeToString(svg);
  } catch {
    return `<div style="font-size:9px;color:#999">⚠ Geen barcode</div>`;
  }
}

function buildLabelHtml(art, opts, fmtCfg) {
  const {
    show_art_code = true, show_omschrijving = true, show_prijs = true, show_ean = true,
    prijsGroep = 'basic',
  } = opts;
  const ean = (art.ean || '').trim();
  // Fallback: geen EAN → gebruik art_code als Code128 barcode (renderer detecteert automatisch)
  const barcodeValue = ean || (art.art_code || '').trim();
  const prijs = (art.prijzen || {})[prijsGroep] || art.prijzen?.basic || 0;
  const w = fmtCfg.w, h = fmtCfg.h;
  return `
    <div class="label" style="width:${w}mm;height:${h}mm;padding:1.5mm;box-sizing:border-box;display:flex;flex-direction:column;justify-content:center;overflow:hidden;page-break-inside:avoid">
      ${show_art_code && art.art_code ? `<div style="font-family:monospace;font-size:8pt;font-weight:700;color:#002FA7;line-height:1.1">${art.art_code}</div>` : ''}
      ${show_omschrijving && art.omschrijving ? `<div style="font-family:'Segoe UI',Arial,sans-serif;font-size:8pt;color:#111;line-height:1.15;margin-top:0.5mm;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${art.omschrijving}</div>` : ''}
      <div style="flex:1;display:flex;align-items:center;justify-content:center;margin-top:0.8mm">
        ${show_ean && barcodeValue ? renderBarcodeSvg(barcodeValue) : `<div style="font-size:7pt;color:#bbb">${show_ean ? 'Geen code' : ''}</div>`}
      </div>
      ${show_prijs && prijs > 0 ? `<div style="font-family:'Segoe UI',Arial,sans-serif;font-size:10pt;font-weight:800;text-align:right;color:#000;line-height:1;margin-top:0.5mm">€ ${Number(prijs).toFixed(2).replace('.', ',')}</div>` : ''}
    </div>`;
}

function buildDocumentHtml(items, fmtKey, opts) {
  const cfg = LABEL_FORMATS[fmtKey];
  const labels = items.flatMap(a => Array(opts.copies || 1).fill(a));
  if (cfg.type === 'a4') {
    const { w, h, cols, rows, mLeft = 7, mTop = 15, gapX = 2.5, gapY = 0 } = cfg;
    // Per pagina max cols*rows labels
    const perPage = cols * rows;
    const pages = [];
    for (let p = 0; p < labels.length; p += perPage) {
      const chunk = labels.slice(p, p + perPage);
      const cells = chunk.map(a => buildLabelHtml(a, opts, cfg)).join('');
      pages.push(`<div class="sheet" style="width:210mm;height:297mm;padding:${mTop}mm ${mLeft}mm;box-sizing:border-box;page-break-after:always">
        <div style="display:grid;grid-template-columns:repeat(${cols}, ${w}mm);grid-auto-rows:${h}mm;column-gap:${gapX}mm;row-gap:${gapY}mm">${cells}</div>
      </div>`);
    }
    return `<!DOCTYPE html><html><head><title>Barcode labels</title>
<style>
  @page { size: A4; margin: 0; }
  body { margin: 0; font-family: 'Segoe UI',Arial,sans-serif; }
  .label { border: 0.2mm dashed #eee; }
  @media print { .label { border: 0; } .no-print { display:none } }
</style></head><body>
<div class="no-print" style="padding:14px;background:#002FA7;color:#fff;text-align:center;font-size:13px">
  <strong>${labels.length}</strong> label(s) · ${cfg.label} · <button onclick="window.print()" style="margin-left:10px;padding:6px 14px;background:#fff;color:#002FA7;border:none;font-weight:700;cursor:pointer">Afdrukken</button>
</div>
${pages.join('')}
</body></html>`;
  }
  // Roll / continuous — één label per pagina
  const pages = labels.map(a => `<div class="page" style="width:${cfg.w}mm;height:${cfg.h}mm;page-break-after:always;box-sizing:border-box">${buildLabelHtml(a, opts, cfg)}</div>`).join('');
  return `<!DOCTYPE html><html><head><title>Barcode labels</title>
<style>
  @page { size: ${cfg.w}mm ${cfg.h}mm; margin: 0; }
  body { margin: 0; font-family: 'Segoe UI',Arial,sans-serif; }
  @media print { .no-print { display:none } }
</style></head><body>
<div class="no-print" style="padding:14px;background:#002FA7;color:#fff;text-align:center;font-size:13px">
  <strong>${labels.length}</strong> label(s) · ${cfg.label} · <button onclick="window.print()" style="margin-left:10px;padding:6px 14px;background:#fff;color:#002FA7;border:none;font-weight:700;cursor:pointer">Afdrukken</button>
</div>
${pages}
</body></html>`;
}

export default function LabelExportModal({ open, onClose, selectedArtikels = [], allArtikels = [], prijsGroepen = [] }) {
  const [fmtKey, setFmtKey] = useState('dymo_99012');
  const [customW, setCustomW] = useState(60);
  const [customH, setCustomH] = useState(30);
  const [source, setSource] = useState(selectedArtikels.length > 0 ? 'selected' : 'all');
  const [onlyWithEan, setOnlyWithEan] = useState(false);
  const [copies, setCopies] = useState(1);
  const [showArtCode, setShowArtCode] = useState(true);
  const [showOmschrijving, setShowOmschrijving] = useState(true);
  const [showPrijs, setShowPrijs] = useState(true);
  const [showEan, setShowEan] = useState(true);
  const [prijsGroep, setPrijsGroep] = useState(prijsGroepen[0]?.key || 'basic');

  const items = useMemo(() => {
    const base = source === 'selected' && selectedArtikels.length ? selectedArtikels : allArtikels;
    return onlyWithEan ? base.filter(a => (a.ean || '').trim()) : base;
  }, [source, selectedArtikels, allArtikels, onlyWithEan]);

  const totalLabels = items.length * (parseInt(copies) || 1);
  const cfgForOutput = fmtKey === 'custom' ? { ...LABEL_FORMATS.custom, w: parseFloat(customW) || 60, h: parseFloat(customH) || 30 } : LABEL_FORMATS[fmtKey];

  const doExport = (mode) => {
    if (items.length === 0) { alert('Geen artikelen om te exporteren.'); return; }
    const opts = {
      show_art_code: showArtCode, show_omschrijving: showOmschrijving, show_prijs: showPrijs, show_ean: showEan,
      copies: parseInt(copies) || 1, prijsGroep,
    };
    // Vervang LABEL_FORMATS.custom tijdelijk met custom-dimensies
    const origCustom = { ...LABEL_FORMATS.custom };
    if (fmtKey === 'custom') { LABEL_FORMATS.custom = cfgForOutput; }
    const html = buildDocumentHtml(items, fmtKey === 'custom' ? 'custom' : fmtKey, opts);
    if (fmtKey === 'custom') { LABEL_FORMATS.custom = origCustom; }

    if (mode === 'print') {
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
      // auto-open print dialog
      setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 500);
    } else {
      // Download als HTML (kan direct worden geopend en via browser naar PDF)
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `barcode-labels-${new Date().toISOString().slice(0, 10)}.html`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (!open) return null;
  const cfg = LABEL_FORMATS[fmtKey];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="label-export-modal">
      <div className="bg-white w-[min(720px,95vw)] max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2"><Tag size={20} className="text-[#002FA7]" weight="duotone" /><h3 className="font-semibold text-sm">Barcode-labels exporteren</h3></div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-900" data-testid="label-export-close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Bron */}
          <div>
            <label className="form-label">Welke artikelen?</label>
            <div className="flex gap-2">
              <label className={`flex-1 border px-3 py-2 cursor-pointer text-sm ${source === 'selected' ? 'border-[#002FA7] bg-[#F0F4FF] text-[#002FA7] font-semibold' : 'border-gray-300'}`}>
                <input type="radio" className="mr-2" checked={source === 'selected'} onChange={() => setSource('selected')} data-testid="label-src-selected" disabled={selectedArtikels.length === 0} />
                Geselecteerde ({selectedArtikels.length})
              </label>
              <label className={`flex-1 border px-3 py-2 cursor-pointer text-sm ${source === 'all' ? 'border-[#002FA7] bg-[#F0F4FF] text-[#002FA7] font-semibold' : 'border-gray-300'}`}>
                <input type="radio" className="mr-2" checked={source === 'all'} onChange={() => setSource('all')} data-testid="label-src-all" />
                Alle zichtbare ({allArtikels.length})
              </label>
            </div>
          </div>

          {/* Formaat */}
          <div>
            <label className="form-label">Label-formaat</label>
            <select value={fmtKey} onChange={e => setFmtKey(e.target.value)} className="field-input" data-testid="label-format">
              <optgroup label="Rol-printers (Dymo / Brother / Zebra)">
                {Object.entries(LABEL_FORMATS).filter(([, v]) => v.type === 'roll').map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </optgroup>
              <optgroup label="A4-vellen (Avery)">
                {Object.entries(LABEL_FORMATS).filter(([, v]) => v.type === 'a4').map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </optgroup>
              <optgroup label="Custom">
                <option value="custom">Aangepast formaat…</option>
              </optgroup>
            </select>
            {cfg.note && fmtKey !== 'custom' && <div className="text-[11px] text-gray-500 mt-1">{cfg.note}</div>}
            {fmtKey === 'custom' && (
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="label-custom-dims">
                <div>
                  <label className="text-[11px] text-gray-500">Breedte (mm)</label>
                  <input type="number" className="field-input" value={customW} onChange={e => setCustomW(e.target.value)} step="0.1" />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Hoogte (mm)</label>
                  <input type="number" className="field-input" value={customH} onChange={e => setCustomH(e.target.value)} step="0.1" />
                </div>
              </div>
            )}
          </div>

          {/* Inhoud */}
          <div>
            <label className="form-label">Toon op label</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 text-xs"><IOSToggle checked={showArtCode} onChange={setShowArtCode} size="sm" testid="label-show-code" /> Art. code</div>
              <div className="flex items-center gap-2 text-xs"><IOSToggle checked={showOmschrijving} onChange={setShowOmschrijving} size="sm" testid="label-show-om" /> Omschrijving</div>
              <div className="flex items-center gap-2 text-xs"><IOSToggle checked={showEan} onChange={setShowEan} size="sm" testid="label-show-ean" /> EAN-barcode</div>
              <div className="flex items-center gap-2 text-xs"><IOSToggle checked={showPrijs} onChange={setShowPrijs} size="sm" testid="label-show-prijs" /> Prijs</div>
            </div>
          </div>

          {/* Opties */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-gray-500">Prijsgroep</label>
              <select value={prijsGroep} onChange={e => setPrijsGroep(e.target.value)} className="field-input" disabled={!showPrijs}>
                {(prijsGroepen.length ? prijsGroepen : [{ key: 'basic', label: 'Basic' }]).map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Kopieën per artikel</label>
              <input type="number" value={copies} onChange={e => setCopies(e.target.value)} className="field-input" min="1" max="100" data-testid="label-copies" />
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-2 text-xs h-[38px]" title="Als UIT → artikels zonder EAN krijgen de art-code als Code128 barcode">
                <IOSToggle checked={onlyWithEan} onChange={setOnlyWithEan} size="sm" testid="label-only-ean" />
                Enkel artikels met EAN
              </div>
            </div>
          </div>

          {/* Waarschuwing als filter alles wegneemt */}
          {onlyWithEan && items.length === 0 && (
            <div className="px-3 py-2 bg-amber-50 border-l-4 border-amber-400 text-xs text-amber-900" data-testid="label-ean-warning">
              <strong>Geen artikels met EAN gevonden.</strong> Schakel de toggle <em>"Enkel artikels met EAN"</em> uit — dan wordt de artikel-code automatisch als Code128-barcode afgedrukt (werkt ook met elke scanner).
            </div>
          )}

          {/* Samenvatting */}
          <div className="px-3 py-2 bg-[#F0F4FF] border-l-4 border-[#002FA7] text-sm">
            <strong>{totalLabels}</strong> label(s) klaar voor export — {items.length} artikel(en) × {copies || 1} kopie(ën)
            {!onlyWithEan && items.some(a => !(a.ean || '').trim()) && (
              <div className="text-[11px] text-gray-600 mt-0.5">
                Artikels zonder EAN krijgen hun <strong>art-code als Code128-barcode</strong>.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm" data-testid="label-cancel">Annuleren</button>
          <button type="button" onClick={() => doExport('download')} className="btn btn-secondary btn-sm inline-flex items-center gap-1.5" data-testid="label-download"><DownloadSimple size={15} /> Download HTML</button>
          <button type="button" onClick={() => doExport('print')} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="label-print" disabled={totalLabels === 0}><Printer size={15} /> Afdrukken / PDF</button>
        </div>
      </div>
    </div>
  );
}
